const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });

const blobFromCanvas = (
  canvas: HTMLCanvasElement,
  type: string
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Failed to crop image")),
      type,
      1
    );
  });

export const cropImage = async (
  file: File,
  targetAspectRatio: number = 16 / 9
): Promise<File> => {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const { naturalWidth: w, naturalHeight: h } = img;
    const srcAspect = w / h;

    let sx = 0;
    let sy = 0;
    let sw = w;
    let sh = h;

    if (srcAspect > targetAspectRatio) {
      // Image is wider than target — crop the sides
      sw = h * targetAspectRatio;
      sx = (w - sw) / 2;
    } else if (srcAspect < targetAspectRatio) {
      // Image is taller than target — crop the top and bottom
      sh = w / targetAspectRatio;
      sy = (h - sh) / 2;
    } else {
      // Already correct aspect ratio — no crop needed
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Failed to get canvas context for cropping");

    context.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);

    const blob = await blobFromCanvas(canvas, file.type);
    return new File([blob], file.name, { type: file.type });
  } catch (error) {
    // Log the error and return the original file on failure
    console.error("Error cropping image:", error);
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
};
